import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, CatalogProductWithVendor, Vendor, CreateCatalogProductData } from '../api';
import Layout from '../components/Layout';

function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className={`card p-6 w-full ${sizeClasses[size]} relative z-10 animate-slide-up max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold text-ink-50">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const CATEGORIES = [
  'shirt',
  'hoodie',
  'sweatshirt',
  'pants',
  'shorts',
  'jacket',
  'hat',
  'bag',
  'accessory',
  'beauty',
  'other',
];

export default function AdminCatalog() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<CatalogProductWithVendor[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterVendor, setFilterVendor] = useState<number | ''>('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<CreateCatalogProductData>({
    vendor_catalog_product_id: '',
    vendor_id_fk: 0,
    title: '',
    category: 'shirt',
    base_cost: 0,
    min_retail: 0,
    suggested_retail: 0,
    moq: 1,
    status: 'active',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterVendor, filterCategory, filterStatus]);

  const loadData = async () => {
    try {
      const [productsRes, vendorsRes] = await Promise.all([
        api.getCatalogProducts({
          vendor_id_fk: filterVendor || undefined,
          category: filterCategory || undefined,
          status: filterStatus || undefined,
        }),
        api.getVendors(),
      ]);
      setProducts(productsRes.products);
      setVendors(vendorsRes.vendors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormData({
      vendor_catalog_product_id: '',
      vendor_id_fk: vendors[0]?.id || 0,
      title: '',
      category: 'shirt',
      base_cost: 0,
      min_retail: 0,
      suggested_retail: 0,
      moq: 1,
      status: 'active',
    });
    setFormError('');
    setShowCreateModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      await api.createCatalogProduct(formData);
      setShowCreateModal(false);
      loadData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create product');
    } finally {
      setFormLoading(false);
    }
  };

  const toggleStatus = async (product: CatalogProductWithVendor) => {
    try {
      await api.updateCatalogProduct(product.id, {
        status: product.status === 'active' ? 'inactive' : 'active',
      });
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-ink-50">Product Catalog</h1>
            <p className="text-ink-400 mt-1">Manage vendor catalog products</p>
          </div>
          <button onClick={openCreateModal} className="btn-primary">
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Product
          </button>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="card p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Vendor</label>
              <select
                className="input w-full"
                value={filterVendor}
                onChange={(e) => setFilterVendor(e.target.value ? parseInt(e.target.value) : '')}
              >
                <option value="">All Vendors</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Category</label>
              <select
                className="input w-full"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Status</label>
              <select
                className="input w-full"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterVendor('');
                  setFilterCategory('');
                  setFilterStatus('');
                }}
                className="btn-ghost w-full"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.length === 0 ? (
            <div className="col-span-full card p-12 text-center text-ink-400">
              No products found. Click "Add Product" to create your first one.
            </div>
          ) : (
            products.map((product) => (
              <div key={product.id} className="card overflow-hidden hover:ring-1 hover:ring-accent-500/30 transition-all">
                {/* Placeholder image */}
                <div className="aspect-square bg-ink-800 flex items-center justify-center">
                  <svg className="w-16 h-16 text-ink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-ink-100 line-clamp-1">{product.title}</h3>
                      <p className="text-sm text-ink-400">{product.vendor_name}</p>
                    </div>
                    <span
                      className={`status-badge ${
                        product.status === 'active' ? 'status-badge-active' : 'status-badge-inactive'
                      }`}
                    >
                      {product.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <span className="text-xs bg-ink-800 text-ink-300 px-2 py-0.5 rounded">
                      {product.category}
                    </span>
                    {product.customizable ? (
                      <span className="text-xs bg-accent-500/20 text-accent-400 px-2 py-0.5 rounded">
                        Customizable
                      </span>
                    ) : null}
                    {product.printing_available ? (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                        Print
                      </span>
                    ) : null}
                    {product.embroidery_available ? (
                      <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                        Embroidery
                      </span>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                    <div>
                      <div className="text-ink-500">Base</div>
                      <div className="text-ink-200 font-medium">{formatCurrency(product.base_cost)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500">Min</div>
                      <div className="text-ink-200 font-medium">{formatCurrency(product.min_retail)}</div>
                    </div>
                    <div>
                      <div className="text-ink-500">Suggested</div>
                      <div className="text-accent-400 font-medium">{formatCurrency(product.suggested_retail)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/catalog/${product.id}`)}
                      className="btn-ghost text-sm flex-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleStatus(product)}
                      className="btn-ghost text-sm"
                    >
                      {product.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add Catalog Product"
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-3 text-accent-400 text-sm">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Product ID *</label>
              <input
                type="text"
                className="input w-full"
                value={formData.vendor_catalog_product_id}
                onChange={(e) => setFormData({ ...formData, vendor_catalog_product_id: e.target.value })}
                placeholder="la-splits-hoodie-001"
                required
              />
              <p className="text-xs text-ink-500 mt-1">Unique identifier for Shopify tags</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Vendor *</label>
              <select
                className="input w-full"
                value={formData.vendor_id_fk}
                onChange={(e) => setFormData({ ...formData, vendor_id_fk: parseInt(e.target.value) })}
                required
              >
                <option value="">Select vendor...</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Title *</label>
              <input
                type="text"
                className="input w-full"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Premium Heavyweight Hoodie"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Category *</label>
              <select
                className="input w-full"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Description</label>
            <textarea
              className="input w-full"
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Product description visible to influencers..."
            />
          </div>

          <div className="border-t border-ink-800 pt-4 mt-4">
            <h3 className="text-sm font-medium text-ink-200 mb-3">Pricing</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Base Cost *</label>
                <input
                  type="number"
                  step="0.01"
                  className="input w-full"
                  value={formData.base_cost}
                  onChange={(e) => setFormData({ ...formData, base_cost: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Min Retail *</label>
                <input
                  type="number"
                  step="0.01"
                  className="input w-full"
                  value={formData.min_retail}
                  onChange={(e) => setFormData({ ...formData, min_retail: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Suggested Retail *</label>
                <input
                  type="number"
                  step="0.01"
                  className="input w-full"
                  value={formData.suggested_retail}
                  onChange={(e) => setFormData({ ...formData, suggested_retail: parseFloat(e.target.value) })}
                  required
                />
              </div>
            </div>
          </div>

          <div className="border-t border-ink-800 pt-4 mt-4">
            <h3 className="text-sm font-medium text-ink-200 mb-3">Customization Options</h3>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!formData.customizable}
                  onChange={(e) => setFormData({ ...formData, customizable: e.target.checked ? 1 : 0 })}
                  className="w-4 h-4 rounded border-ink-600 text-accent-500 focus:ring-accent-500"
                />
                <span className="text-sm text-ink-300">Customizable</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!formData.printing_available}
                  onChange={(e) => setFormData({ ...formData, printing_available: e.target.checked ? 1 : 0 })}
                  className="w-4 h-4 rounded border-ink-600 text-accent-500 focus:ring-accent-500"
                />
                <span className="text-sm text-ink-300">Printing Available</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!formData.embroidery_available}
                  onChange={(e) => setFormData({ ...formData, embroidery_available: e.target.checked ? 1 : 0 })}
                  className="w-4 h-4 rounded border-ink-600 text-accent-500 focus:ring-accent-500"
                />
                <span className="text-sm text-ink-300">Embroidery Available</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">MOQ</label>
              <input
                type="number"
                className="input w-full"
                value={formData.moq}
                onChange={(e) => setFormData({ ...formData, moq: parseInt(e.target.value) || 1 })}
                min={1}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Tags</label>
              <input
                type="text"
                className="input w-full"
                value={formData.tags || ''}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="premium, bestseller"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowCreateModal(false)} className="btn-ghost">
              Cancel
            </button>
            <button type="submit" disabled={formLoading} className="btn-primary">
              {formLoading ? 'Creating...' : 'Create Product'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}
